import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import type {
  HypothesesHypothesisListSort as HypothesisListSort,
  HypothesesHypothesisAdjustRequest as AdjustRequest,
  HypothesesHypothesisApproveAdjustmentRequest as ApproveAdjustmentRequest,
  HypothesesHypothesisAssignRequest as AssignRequest,
  HypothesesHypothesisConclusionCreate as ConclusionCreate,
  HypothesesHypothesisConfirmConclusionRequest as ConfirmConclusionRequest,
  HypothesesHypothesisDeclineAdjustmentRequest as DeclineAdjustmentRequest,
  HypothesesHypothesisOverrideKind as OverrideKind,
  HypothesesHypothesisOverrideRequest as OverrideRequest,
  HypothesesHypothesisPauseRequest as PauseRequest,
  HypothesesHypothesisResumeRequest as ResumeRequest,
  HypothesesHypothesisSendBackConclusionRequest as SendBackConclusionRequest,
  HypothesesHypothesisStartRequest as StartRequest,
  HypothesesHypothesisStopReason as StopReason,
  HypothesesHypothesisStopRequest as StopRequest,
  HypothesesHypothesisCounts as HypothesisCounts,
  HypothesesHypothesisNeedsMoreRequest as NeedsMoreRequest,
  HypothesesHypothesisNote as HypothesisNote,
  HypothesesHypothesisParkRequest as ParkRequest,
  HypothesesHypothesisRejectReason as RejectReason,
  HypothesesHypothesisRejectRequest as RejectRequest,
  HypothesesHypothesisResponse as HypothesisResponse,
  HypothesesHypothesisReviewCheck as ReviewCheck,
  HypothesesHypothesisSelectRequest as SelectRequest,
  HypothesesHypothesisShortenDeadlineRequest as ShortenDeadlineRequest,
  HypothesesHypothesisStartReviewRequest as StartReviewRequest,
  HypothesesHypothesisStatusType as HypothesisStatusType,
  HypothesesHypothesisTransition as HypothesisTransition,
  HypothesesHypothesisUnparkRequest as UnparkRequest,
  HypothesesHypothesisWithdrawRequest as WithdrawRequest,
  HypothesesAdminUserRef as AdminUserRef,
  HypothesesConclusionNoticeCreate as ConclusionNoticeCreate,
  HypothesesHypothesisMergeRequest as MergeRequest,
} from '../generated/api/types.gen.ts';
import {
  hypothesesAcceptOwnership,
  hypothesesAddNote,
  hypothesesAdjust,
  hypothesesApproveAdjustment,
  hypothesesAssign,
  hypothesesConclude,
  hypothesesConfirmConclusion,
  hypothesesCounts,
  hypothesesDeclineAdjustment,
  hypothesesDeclineOwnership,
  hypothesesOverride,
  hypothesesPause,
  hypothesesResume,
  hypothesesSendBackConclusion,
  hypothesesStart,
  hypothesesStop,
  hypothesesList,
  hypothesesListNotes,
  hypothesesMeasurementReady,
  hypothesesNeedsMore,
  hypothesesPark,
  hypothesesReject,
  hypothesesSelect,
  hypothesesShortenDeadline,
  hypothesesStartReview,
  hypothesesUnpark,
  hypothesesVerifySource,
  hypothesesWithdraw,
  hypothesesUpdate,
  hypothesesGet,
  hypothesesSendConclusionNotice,
  hypothesesSkipConclusionNotice,
  hypothesesRemoveEvidence,
  hypothesesRemoveExternalLink,
  hypothesisMemoryMerge,
} from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { NYE_TAK, POLL_MS, useNyeSiden, utenRekkefolge, type SondeRad } from './nye.ts';
import { hypoteseNoekkel, type Hypothesis, type HypothesisStatus, type HypothesisSummary, type HypothesisUpdate } from './useHypotheses.ts';

export type { AdjustRequest, AdminUserRef, HypothesisCounts, HypothesisNote, HypothesisResponse, HypothesisStatusType, HypothesisTransition, OverrideKind, RejectReason, ReviewCheck, StopReason };

/**
 * Rommet Hypoteser, behandlerens og beslutningstakerens side (H2.3): innboksen, «Til deg»,
 * notatene og overgangene fra Mottatt og Vurderes. Innsenderens side er `useHypotheses.ts`
 * (H2.2); nøklene deler prefikset `hypotheses`, så en overgang som invaliderer det, frisker
 * både innboksen og «Mine hypoteser». Hver nøkkel under eies her og bare her
 * (`react-ts-integration`, «Query-nøkler har én eier»).
 */
export const behandlingNoekkel = {
  liste: (filter: HypoteseFilter) => ['hypotheses', 'liste', 'filter', filter] as const,
  counts: ['hypotheses', 'counts'] as const,
  notater: (id: string) => ['hypotheses', 'notater', id] as const,
  /** Sonden for stripa (DD-74): de nyeste med samme filter, uten rekkefølgen — den er alltid «nyest». */
  nye: (filter: Omit<HypoteseFilter, 'sort'>) => ['hypotheses', 'nye', filter] as const,
};

/**
 * Filtrene kontrakten har (`HypothesisListQuery`). Rekkefølgen er tjenerens: «Avgjør nå» først
 * (H6), eller synkende innsendt-tid med `sort: 'nyest'` (DD-74).
 */
export interface HypoteseFilter {
  status?: HypothesisStatus;
  statusType?: HypothesisStatusType;
  assignedToMe?: boolean;
  awaitingSubmitter?: boolean;
  dueNow?: boolean;
  /** Løpenummeret, «142» eller «H-142». Tjeneren tolker prefikset og ser bort fra status. */
  number?: string;
  /**
   * Settes bare for «nyest» og — med «Menneskesatte tall» på — én faktor (H3.2, svar 4), så nøkkelen
   * for fristrekkefølgen er den samme som før valget fantes.
   */
  sort?: HypothesisListSort;
}

const SIDE = 50;

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Lista, side for side med kontraktens cursor. Poller aldri: nye meldes av `useNyeHypoteser` (DD-74). */
export function useHypoteseListe(filter: HypoteseFilter, enabled = true) {
  const klient = useApiKlient();
  return useInfiniteQuery({
    queryKey: behandlingNoekkel.liste(filter),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await hypothesesList({ client: klient, query: { ...filter, cursor: pageParam, limit: SIDE } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as { items: HypothesisSummary[]; nextCursor?: string };
    },
    getNextPageParam: (siste) => siste.nextCursor ?? undefined,
  });
}

/**
 * Hvor mange hypoteser som er sendt inn siden lista ble hentet (DD-74, M4.12): stripa
 * «↑ 1 ny hypotese». Samme mekanisme som `useNyeSaker`; tiden er innsendt-tiden, og
 * opprettet-tiden for et utkast, som tjenerens «nyest».
 */
export function useNyeHypoteser(filter: HypoteseFilter, liste: { rader: readonly HypothesisSummary[]; hentet: number }, aktiv: boolean) {
  const klient = useApiKlient();
  const utenSort = utenRekkefolge(filter);
  const sonde = useQuery({
    queryKey: behandlingNoekkel.nye(utenSort),
    enabled: aktiv,
    // Fokus skal alltid spørre på nytt; klientens globale staleTime ville holdt igjen i et minutt.
    staleTime: 0,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<SondeRad[]> => {
      const res = await hypothesesList({ client: klient, query: { ...utenSort, sort: 'nyest', limit: NYE_TAK } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: HypothesisSummary[] }).items.map(tilSonde);
    },
  });
  const { refetch } = sonde;
  useEffect(() => {
    if (aktiv && liste.hentet) void refetch();
  }, [aktiv, liste.hentet, refetch]);
  const listeRader = useMemo(() => liste.rader.map(tilSonde), [liste.rader]);
  return useNyeSiden({ rader: listeRader, hentet: liste.hentet }, { rader: sonde.data, hentet: sonde.dataUpdatedAt }, aktiv);
}

const tilSonde = (h: HypothesisSummary): SondeRad => ({ id: h.id, tid: h.submittedAt ?? h.createdAt });

/**
 * Tellerne i innboksens topp og merket i sidemenyen (`GET /hypotheses/counts`). Poller hvert
 * minutt mens fanen er synlig, og ved fokus, som `useInboxCounts` (DD-74): et merke som står
 * stille mens det kommer hypoteser, er det merket finnes for å unngå.
 */
export function useHypoteseTellinger(enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: behandlingNoekkel.counts,
    // Fokus spør på nytt med det samme, så telleren og stripa aldri sier to ulike ting (DD-74).
    staleTime: 0,
    enabled,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<HypothesisCounts> => {
      const res = await hypothesesCounts({ client: klient });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as HypothesisCounts;
    },
  });
}

/**
 * Notatene på én hypotese (DD-23, DD-66). Bak `hypoteser:leser` med vilje (Kh7): kalles aldri
 * for en bidragsyter — `enabled` er rollen, og tjeneren nekter uansett.
 */
export function useHypoteseNotater(id: string | null, enabled: boolean) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: behandlingNoekkel.notater(id ?? ''),
    enabled: enabled && id !== null,
    queryFn: async (): Promise<HypothesisNote[]> => {
      const res = await hypothesesListNotes({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as HypothesisNote[];
    },
  });
}

export function useLeggTilHypoteseNotat() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }): Promise<HypothesisNote> => {
      const res = await hypothesesAddNote({ client: klient, path: { id }, body: { text } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as HypothesisNote;
    },
    onSuccess: (_n, { id }) => {
      void queryClient.invalidateQueries({ queryKey: behandlingNoekkel.notater(id) });
    },
  });
}

/**
 * Overgangene og handlingene på en hypotese, i én union, så siden kan holde ett kall tilbake i
 * angre-vinduet uten å vite hvilket det er (ux-flow-architect: append-only overganger holdes i
 * klienten; det finnes ikke noe angre-endepunkt). Fra Mottatt og Vurderes (første runde), og etter
 * Valgt (andre runde): «Gi videre», eierskapet, justeringen, overstyringen, start, pause og
 * fortsett, konklusjonen (eieren skriver, beslutningstakeren bekrefter eller sender tilbake) og
 * stopp — og innsenderens «Trekk hypotesen …» (H2.2). Kroppen er kontraktens; uten kropp der kontrakten ikke har en. Funn (`addFinding`) er
 * ikke her: kallet gir et funn, ikke hypotesen.
 */
export type Overgang =
  | { handling: 'startReview'; body: StartReviewRequest }
  | { handling: 'needsMore'; body: NeedsMoreRequest }
  | { handling: 'reject'; body: RejectRequest }
  | { handling: 'park'; body: ParkRequest }
  | { handling: 'unpark'; body: UnparkRequest }
  | { handling: 'select'; body: SelectRequest }
  | { handling: 'shortenDeadline'; body: ShortenDeadlineRequest }
  | { handling: 'acceptOwnership' }
  | { handling: 'declineOwnership' }
  | { handling: 'measurementReady' }
  | { handling: 'verifySource' }
  | { handling: 'assign'; body: AssignRequest }
  | { handling: 'adjust'; body: AdjustRequest }
  | { handling: 'approveAdjustment'; body: ApproveAdjustmentRequest }
  | { handling: 'declineAdjustment'; body: DeclineAdjustmentRequest }
  | { handling: 'override'; body: OverrideRequest }
  | { handling: 'start'; body: StartRequest }
  | { handling: 'pause'; body: PauseRequest }
  | { handling: 'resume'; body: ResumeRequest }
  | { handling: 'conclude'; body: ConclusionCreate }
  | { handling: 'confirmConclusion'; body: ConfirmConclusionRequest }
  | { handling: 'sendBackConclusion'; body: SendBackConclusionRequest }
  | { handling: 'stop'; body: StopRequest }
  | { handling: 'withdraw'; body: WithdrawRequest }
  /**
   * H3.1: notisen til sluttbrukerne bak grunnlaget, og «Ingen notis». Begge er endelige — én
   * avgjørelse per hypotese — og holdes derfor tilbake i angre-vinduet som overgangene. Notisen
   * gir `ConclusionNotice`, ikke hypotesen; hypotesen hentes etterpå, så detaljen viser
   * kvitteringen og lista mister merket «notis til brukerne».
   */
  | { handling: 'sendNotice'; body: ConclusionNoticeCreate }
  | { handling: 'skipNotice' }
  /** H3.1: en sak ut av grunnlaget, fra hypotesen. Holdes tilbake som resten; svaret er hypotesen hentet på nytt. */
  | { handling: 'removeEvidence'; submissionId: string }
  /**
   * H3.3, overgang 13: hypotesen i stien slås sammen med `targetId`, som fører videre. Ett svar til
   * begge innsenderne, så den holdes tilbake i angre-vinduet som de andre (det finnes ikke noe
   * angre-endepunkt). Svaret er MÅLET.
   */
  | { handling: 'merge'; body: MergeRequest }
  /**
   * H3.5: en ekstern lenke ut av hypotesen. Fjerningen er endelig hos tjeneren, så klienten holder
   * den tilbake i angre-vinduet (DD-70) som resten; det finnes ikke noe angre-endepunkt. Navnet står
   * bare for toasten. Svaret er hypotesen hentet på nytt.
   */
  | { handling: 'removeLink'; linkId: string; label: string };

export function useHypoteseOvergang() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, overgang }: { id: string; overgang: Overgang }): Promise<Hypothesis> => {
      const path = { id };
      const client = klient;
      let res: { response?: Response; data?: unknown; error?: unknown };
      switch (overgang.handling) {
        case 'startReview':
          res = await hypothesesStartReview({ client, path, body: overgang.body });
          break;
        case 'needsMore':
          res = await hypothesesNeedsMore({ client, path, body: overgang.body });
          break;
        case 'reject':
          res = await hypothesesReject({ client, path, body: overgang.body });
          break;
        case 'park':
          res = await hypothesesPark({ client, path, body: overgang.body });
          break;
        case 'unpark':
          res = await hypothesesUnpark({ client, path, body: overgang.body });
          break;
        case 'select':
          res = await hypothesesSelect({ client, path, body: overgang.body });
          break;
        case 'shortenDeadline':
          res = await hypothesesShortenDeadline({ client, path, body: overgang.body });
          break;
        case 'acceptOwnership':
          res = await hypothesesAcceptOwnership({ client, path });
          break;
        case 'declineOwnership':
          res = await hypothesesDeclineOwnership({ client, path });
          break;
        case 'measurementReady':
          res = await hypothesesMeasurementReady({ client, path });
          break;
        case 'verifySource':
          res = await hypothesesVerifySource({ client, path });
          break;
        case 'assign':
          res = await hypothesesAssign({ client, path, body: overgang.body });
          break;
        case 'adjust':
          res = await hypothesesAdjust({ client, path, body: overgang.body });
          break;
        case 'approveAdjustment':
          res = await hypothesesApproveAdjustment({ client, path, body: overgang.body });
          break;
        case 'declineAdjustment':
          res = await hypothesesDeclineAdjustment({ client, path, body: overgang.body });
          break;
        case 'override':
          res = await hypothesesOverride({ client, path, body: overgang.body });
          break;
        case 'start':
          res = await hypothesesStart({ client, path, body: overgang.body });
          break;
        case 'pause':
          res = await hypothesesPause({ client, path, body: overgang.body });
          break;
        case 'resume':
          res = await hypothesesResume({ client, path, body: overgang.body });
          break;
        case 'conclude':
          res = await hypothesesConclude({ client, path, body: overgang.body });
          break;
        case 'confirmConclusion':
          res = await hypothesesConfirmConclusion({ client, path, body: overgang.body });
          break;
        case 'sendBackConclusion':
          res = await hypothesesSendBackConclusion({ client, path, body: overgang.body });
          break;
        case 'stop':
          res = await hypothesesStop({ client, path, body: overgang.body });
          break;
        case 'withdraw':
          res = await hypothesesWithdraw({ client, path, body: overgang.body });
          break;
        case 'sendNotice': {
          const sendt = await hypothesesSendConclusionNotice({ client, path, body: overgang.body });
          if (!sendt.response?.ok || !sendt.data) kast(sendt);
          res = await hypothesesGet({ client, path });
          break;
        }
        case 'skipNotice':
          res = await hypothesesSkipConclusionNotice({ client, path });
          break;
        case 'removeEvidence': {
          const fjernet = await hypothesesRemoveEvidence({ client, path: { id, submissionId: overgang.submissionId } });
          if (!fjernet.response?.ok) kast(fjernet);
          res = await hypothesesGet({ client, path });
          break;
        }
        case 'merge':
          res = await hypothesisMemoryMerge({ client, path, body: overgang.body });
          break;
        case 'removeLink': {
          const fjernet = await hypothesesRemoveExternalLink({ client, path: { id, linkId: overgang.linkId } });
          if (!fjernet.response?.ok) kast(fjernet);
          res = await hypothesesGet({ client, path });
          break;
        }
      }
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
    onSuccess: (h, vars) => {
      queryClient.setQueryData(hypoteseNoekkel.detalj(h.id), h);
      if (h.number !== undefined) queryClient.setQueryData(hypoteseNoekkel.nummer(h.number), h);
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'liste'] });
      void queryClient.invalidateQueries({ queryKey: behandlingNoekkel.counts });
      // H3.1: grunnlaget (eies av useHypoteseGrunnlag.ts — samme prefiks) og sakens «Grunnlag for H-142».
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'grunnlag', h.id] });
      void queryClient.invalidateQueries({ queryKey: ['submissions', 'detalj'] });
      // H3.3: en sammenslåing endrer begge (den som ble slått inn, og målet), og kandidatene.
      if (vars.overgang.handling === 'merge') {
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'detalj'] });
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'nummer'] });
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'kandidater'] });
      }
    },
  });
}

/**
 * «Send endringen» (H2.2, W8b): innsenderen endrer en hypotese i mottatt eller vurderes. Samme
 * `PATCH /{id}` som autolagringen av et utkast, men her er hvert kall en revisjon
 * (`innsender_endret`) — tjeneren kjører send-portene, nullstiller `awaitingSubmitter` og lar
 * fristen stå. Svaret legges i cachen under detaljen og nummeret, så «Hypotesen min» viser den
 * nye versjonen uten kortet straks den åpnes; listene og tellerne hentes på nytt.
 */
export function useSendEndringen() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: HypothesisUpdate }): Promise<Hypothesis> => {
      const res = await hypothesesUpdate({ client: klient, path: { id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
    onSuccess: (h) => {
      queryClient.setQueryData(hypoteseNoekkel.detalj(h.id), h);
      if (h.number !== undefined) queryClient.setQueryData(hypoteseNoekkel.nummer(h.number), h);
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'liste'] });
      void queryClient.invalidateQueries({ queryKey: behandlingNoekkel.counts });
    },
  });
}

/**
 * Henter hypotesen på nytt etter at tjeneren sa at noen andre kom først (409
 * `invalid_status_transition`, P10b): detaljen viser da det som faktisk skjedde.
 */
export function useFriskOppHypotese() {
  const queryClient = useQueryClient();
  return (id: string, nummer?: number) => {
    void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.detalj(id) });
    if (nummer !== undefined) void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.nummer(nummer) });
    void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'liste'] });
    void queryClient.invalidateQueries({ queryKey: behandlingNoekkel.counts });
  };
}
